import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LocationModal } from '../../components/LocationModal';
import { toast } from 'react-toastify';
import styles from './EventRegister.module.css';
import { 
  getEventDetails, 
  checkUserRegistration, 
  registerForEvent, 
  verifyAndRegisterForEvent,
  EventDetails
} from '../../services/eventRegistrationService';

interface EventDetailsProps {
  Events_ID: string;
  title: string;
  sub_title?: string;
  event_date: string;
  location_name?: string;
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  status?: number;
}

const Event_register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  // Accept both 'verification_code' and 'code' parameters for backward compatibility
  const verification_code = searchParams.get('verification_code') || searchParams.get('code') || '';
  const email = searchParams.get('email') || '';
  const firstName = searchParams.get('firstname') || '';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: email,
    verification_code: verification_code,
    first_name: firstName,
    last_name: '',
  });
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    // Load event details
    if (eventId) {
      fetchEventDetails(String(eventId));
    }

    // Auto-submit logic based on URL parameters
    if (email && verification_code && firstName) {
      setAutoSubmit(true);
      setCurrentStep(2);
      setIsRegistered(true);
    }
  }, [eventId, email, verification_code, firstName]);

  const fetchEventDetails = async (id: string) => {
    setIsLoadingDetails(true);
    setErrorMessage(null);
    try {
      const data = await getEventDetails(id);
      setEventDetails(data);
    } catch (error) {
      console.error('Error fetching event details:', error);
      
      // Check if it's a 404 error
      if (error instanceof Error && error.message.includes('404')) {
        setErrorMessage('Event not found. The event you are looking for does not exist or has been removed.');
      } else if (error instanceof Error) {
        setErrorMessage(`Error loading event details: ${error.message}`);
      } else {
        setErrorMessage('Could not load event details. Please try again later.');
      }
      
      toast.error('Could not load event details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });
  };

  const checkRegistration = async () => {
    if (!eventId) return;
    
    setIsLoading(true);
    try {
      const response = await checkUserRegistration(
        formData.email, 
        formData.verification_code, 
        eventId
      );

      // Verification is now optional, proceed to step 2 in all cases
      if (response.registered && response.needs_verification) {
        toast.info(response.message || 'Verification email sent, but you can continue with registration.');
      }
      
      setIsRegistered(response.registered || false);
      setCurrentStep(2); // Always go to step 2 (name entry)
    } catch (error) {
      console.error('Error checking registration:', error);
      if (error instanceof Error) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error('An unexpected error occurred during registration check');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const submitForm = async () => {
    if (!eventId) return;
    
    setIsLoading(true);
    try {
      // Always use verifyAndRegisterForEvent which directly registers users
      // without requiring verification completion
      const response = await verifyAndRegisterForEvent(
        formData.email,
        formData.verification_code,
        formData.first_name,
        formData.last_name,
        eventId
      );

      setFormMessage(response.message || 'Registration completed');
      setCurrentStep(3); // Show message
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error instanceof Error) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error('An unexpected error occurred during form submission');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (autoSubmit) {
      submitForm();
    }
  }, [autoSubmit]);

  // Render different UI states
  if (isLoadingDetails) {
    return (
      <div className={`container ${styles.loadingContainer}`}>
        <div className={`spinner-border text-primary ${styles.loadingSpinner}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h3>Loading event details...</h3>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="container">
        <div className={`card p-4 shadow-sm ${styles.roleContainer} ${styles.errorContainer}`}>
          <div className={styles.errorIcon}>
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h4 className="mb-3">Unable to Load Event</h4>
          <div className="alert alert-danger">
            {errorMessage}
          </div>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => eventId && fetchEventDetails(String(eventId))}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!eventDetails) {
    return <div className="container">No event details available.</div>;
  }

  return (
    <div className={`container ${styles.container}`}>
      <div className={`card p-4 shadow-sm ${styles.roleContainer}`}>
        <div className={styles.roleHeader}>
          <i className="bi bi-calendar3-event me-2"></i>
          <h4 className="mb-0">Event - {eventDetails.title}</h4>
          <p>
            <i className="bi bi-info text-primary" 
               data-bs-toggle="popover" 
               data-bs-content="Register for this event by verifying your email and completing your details."
               title="Event Registration">
            </i>
          </p>
        </div>
        
        <h5 className={styles.eventDate}><strong>Event Date: </strong> {new Date(eventDetails.event_date).toLocaleDateString()} at {new Date(eventDetails.event_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</h5>
        
        {eventDetails.location_name && (
          <h5 className={styles.eventDate}>
            <strong>Event Location: </strong> 
            {eventDetails.location_name} | {eventDetails.street} {eventDetails.house_number}, 
            {eventDetails.postal_code} {eventDetails.city}, {eventDetails.country}
          </h5>
        )}

        {/* Location Button */}
        {eventDetails.location_name && eventDetails.street && eventDetails.city && eventDetails.country && (
          <div className={styles.locationContainer}>
            <button 
              className={styles.locationBtn}
              data-bs-toggle="modal" 
              data-bs-target="#locationModal"
              data-location-name={eventDetails.location_name}
              data-street={eventDetails.street}
              data-house-number={eventDetails.house_number}
              data-postal-code={eventDetails.postal_code}
              data-city={eventDetails.city}
              data-country={eventDetails.country}
              title="Show location"
            >
              <i className="bi bi-pin-map"></i> View Location
            </button>
          </div>
        )}
        <br />

        {eventDetails.status === 1 ? ( // Assuming status 1 is "active"
          <div className={styles.formContainer}>
            {/* Step 1: Email and Verification Code */}
            {currentStep === 1 && (
              <div id="step1">
                <h2>Step 1: Email and Code</h2>
                <input 
                  type="email" 
                  id="email" 
                  className={styles.formInput}
                  placeholder="Email" 
                  value={formData.email} 
                  onChange={handleInputChange}
                  required 
                />
                <input 
                  type="text" 
                  id="verification_code" 
                  className={styles.formInput}
                  placeholder="Verification Code" 
                  value={formData.verification_code} 
                  onChange={handleInputChange}
                  required 
                />
                <button 
                  id="check-registration-btn" 
                  className={`btn btn-primary ${isLoading ? styles.disabledButton : ''} ${styles.formButton}`}
                  onClick={checkRegistration}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Checking...
                    </>
                  ) : 'Check'}
                </button>
              </div>
            )}

            {/* Step 2: Name */}
            {currentStep === 2 && (
              <div id="step2">
                <h2>Step 2: Name</h2>
                <input 
                  type="text" 
                  id="first_name" 
                  className={styles.formInput}
                  placeholder="First Name" 
                  value={formData.first_name} 
                  onChange={handleInputChange}
                  required 
                />
                <input 
                  type="text" 
                  id="last_name" 
                  className={styles.formInput}
                  placeholder="Last Name" 
                  value={formData.last_name} 
                  onChange={handleInputChange}
                  required 
                />
                <button 
                  id="submit-form-btn" 
                  className={`btn btn-primary ${isLoading ? styles.disabledButton : ''} ${styles.formButton}`}
                  onClick={submitForm}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Submitting...
                    </>
                  ) : 'Save'}
                </button>
              </div>
            )}

            {/* Final step: Show message */}
            {currentStep === 3 && (
              <div>
                <h3>{formMessage}</h3>
              </div>
            )}
          </div>
        ) : (
          <div className={`d-flex justify-content-center ${styles.badgeContainer}`}>
            <span className="badge text-wrap text-center p-2 bg-warning">
              This event is no longer active.
            </span>
          </div>
        )}
      </div>

      {/* Include Location Modal */}
      <LocationModal />
    </div>
  );
};

export default Event_register;
